import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ChevronDown, ArrowRight } from "lucide-react";

// A warm, saturated colour per category
const COLORS = [
  "#C2603F", "#E0A82E", "#C64B2C", "#7E8B3B", "#D9793A", "#5E7B3A",
  "#B0553A", "#CE8E1E", "#A8532B", "#6F7F35", "#D96A4A", "#B06A2C",
  "#8C6A2B", "#C2843A",
];

// Same giraffe icon for every category
const GiraffeIcon = ({ color = "#9F583F", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <path d="M12 30 C11 24 11 18 14 14" stroke={color} strokeWidth="3.2" strokeLinecap="round" />
    <ellipse cx="19" cy="11" rx="5" ry="4.4" fill={color} />
    <path d="M23 11 q4 0.5 4 4.5" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
    <line x1="16" y1="7.5" x2="15" y2="3.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="14.8" cy="3" r="1.7" fill={color} />
    <line x1="22" y1="7.5" x2="23" y2="3.5" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="23.2" cy="3" r="1.7" fill={color} />
    <circle cx="17.5" cy="10.5" r="1.1" fill="#FBF6F3" />
  </svg>
);

const CoverImg = ({ src, alt, className }) =>
  src ? (
    <img src={mediaUrl(src)} alt={alt} loading="lazy" className={className} />
  ) : (
    <div className={`${className} bg-[#E7C9A0] flex items-center justify-center`}>
      <span className="font-display italic text-xl text-[#9F583F]">Giraffe</span>
    </div>
  );

export default function GiraffeMap() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/posts").then((res) => setPosts(res.data)).catch(() => {});
    api.get("/categories").then((res) => setCats(res.data)).catch(() => {});
  }, []);

  const byCat = useMemo(() => {
    const m = {};
    posts.forEach((p) => {
      const c = (p.category || "").trim().toLowerCase();
      if (!c) return;
      (m[c] = m[c] || []).push(p);
    });
    return m;
  }, [posts]);

  return (
    <div className="relative z-10">
      <Header />

      {/* Full-screen savanna background */}
      <div
        className="relative w-full overflow-hidden"
        style={{ minHeight: "100vh", backgroundColor: "#E3B667" }}
      >
        {/* savanna illustration, fixed cover */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url('/savanna-bg.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            backgroundAttachment: "fixed",
          }}
        />
        {/* warm readability overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(240,200,120,0.30) 0%, rgba(226,170,90,0.5) 55%, rgba(205,145,66,0.62) 100%)" }} />

        {/* foreground giraffe */}
        <img src="/giraffe-marker.png" alt="" aria-hidden="true" className="absolute pointer-events-none select-none hidden lg:block" style={{ right: 8, bottom: 30, height: 360, opacity: 0.96, transform: "scaleX(-1)", filter: "drop-shadow(0 14px 16px rgba(0,0,0,0.32))" }} />

        {/* wooden title board */}
        <div className="relative z-10 flex justify-center pt-14 pb-8 px-6">
          <div
            className="relative text-center rounded-xl px-8 md:px-14 py-6 md:py-8 max-w-2xl w-full"
            style={{
              background: "linear-gradient(180deg,#9A6533 0%,#7A4E27 60%,#6B4322 100%)",
              boxShadow: "0 14px 30px rgba(60,36,18,0.5), inset 0 2px 0 rgba(255,255,255,0.15), inset 0 -6px 14px rgba(0,0,0,0.3)",
              border: "3px solid #5A3A1E",
            }}
          >
            <span className="absolute top-2 left-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <span className="absolute top-2 right-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <span className="absolute bottom-2 left-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <span className="absolute bottom-2 right-3 w-2.5 h-2.5 rounded-full bg-[#3A2413]" />
            <h1 className="font-display font-bold tracking-wide text-4xl md:text-6xl text-[#F5E6C8]" style={{ textShadow: "0 2px 0 rgba(0,0,0,0.35)" }}>
              GIRAFFE MAP
            </h1>
            <p className="font-display text-lg md:text-2xl text-[#EAD3A6] mt-2 tracking-wide">Il Cassetto delle Giraffe</p>
            <p className="label-ui text-[#D8BC8A] mt-1">Il blog di curiosità, storie e idee</p>
          </div>
        </div>

        {/* vertical ordered list */}
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pb-24 space-y-4">
          {cats.map((c, i) => {
            const color = COLORS[i % COLORS.length];
            const items = byCat[c.name.toLowerCase()] || [];
            const isOpen = open === c.name;
            return (
              <div key={c.name} data-testid={`map-category-${c.name}`}>
                <motion.button
                  onClick={() => setOpen(isOpen ? null : c.name)}
                  whileHover={{ x: 4 }}
                  className="w-full text-left rounded-2xl overflow-hidden flex items-stretch shadow-lg"
                  style={{ background: `linear-gradient(90deg, ${color}2E 0%, rgba(255,250,240,0.96) 42%)`, border: `1.5px solid ${color}66` }}
                  data-testid={`map-category-btn-${c.name}`}
                >
                  {/* colored index bar */}
                  <div className="flex items-center justify-center shrink-0" style={{ width: 66, background: color }}>
                    <span className="font-display font-bold text-2xl text-white" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  {/* icon */}
                  <div className="flex items-center pl-4 pr-1">
                    <span className="flex items-center justify-center rounded-full" style={{ width: 46, height: 46, background: `${color}22` }}>
                      <GiraffeIcon color={color} size={26} />
                    </span>
                  </div>
                  {/* text */}
                  <div className="flex-1 min-w-0 py-4 pr-3 pl-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold uppercase tracking-wide text-[#3A2415]" style={{ fontSize: 18 }}>
                        {c.name}
                      </span>
                      {items.length > 0 && (
                        <span className="label-ui text-white rounded-full px-2 leading-5" style={{ fontSize: 10, background: color }}>
                          {items.length}
                        </span>
                      )}
                    </div>
                    <p className="font-body text-[#5A4326] leading-snug mt-1" style={{ fontSize: 13.5 }}>
                      {c.description}
                    </p>
                  </div>
                  {/* chevron */}
                  <div className="flex items-center pr-5" style={{ color }}>
                    <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                      <ChevronDown size={22} />
                    </motion.span>
                  </div>
                </motion.button>

                {/* expanded articles */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="rounded-2xl mt-2 p-5 md:p-7" style={{ background: "rgba(255,255,255,0.7)", border: `1px dashed ${color}80` }} data-testid={`map-panel-${c.name}`}>
                        {items.length === 0 ? (
                          <p className="font-body italic text-[#7A4A22] text-center py-6">
                            Ancora nessun articolo in questa categoria. Torna presto!
                          </p>
                        ) : (
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
                            {items.map((p) => (
                              <Link key={p.id} to={`/articolo/${p.slug}`} data-testid={`map-article-${p.slug}`} className="group flex flex-col">
                                <div className="overflow-hidden mb-4 rounded-lg">
                                  <CoverImg src={p.cover_image} alt={p.title} className="w-full aspect-[3/2] object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                                </div>
                                <h3 className="font-display text-xl leading-tight tracking-tight text-[#1C1917] group-hover:text-[#9F583F] transition-colors">
                                  {p.title}
                                </h3>
                                <p className="font-body text-sm text-[#57534E] mt-2 leading-relaxed line-clamp-2">{p.excerpt}</p>
                                <span className="inline-flex items-center gap-2 mt-3 label-ui transition-colors" style={{ color }}>
                                  Leggi l'articolo <ArrowRight size={13} strokeWidth={1.6} />
                                </span>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}

          {/* closing sign */}
          <div className="pt-6 text-center">
            <p className="font-display text-lg text-[#7A3B18] italic">
              Ogni articolo è un tesoro da scoprire.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
